// __ROUTE_DRAFTS__ (2026-10-03) A job tile in the Route Builder: the stops'
// addresses (business names first), not the load number; hover shows every stop.
// Sortable with @dnd-kit so tiles slide out of the way while one is dragged.
import React, { useRef, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { loadRouteSummary, cardFace } from '../../utils/routeBuilderModel';

const clamp2 = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordBreak: 'break-word',
};

export function JobTile({
  load, selected = false, color = '#95a5a6', driverName = null, routeBadge = null, draftBadges = [],
  seq = null, overlay = false, placeholder = false, onClick, rightSlot = null, groupCount = 0, inProgress = false,
}) {
  const summary = loadRouteSummary(load);
  // __CARD_FACE__ (2026-10-16) customer, then From -> To; the load number only in the hover card / opened job
  const face = cardFace(load);
  const [tip, setTip] = useState(null);
  const timer = useRef(null);
  const ref = useRef(null);

  const showTip = () => {
    if (overlay) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const r = ref.current && ref.current.getBoundingClientRect();
      if (!r) return;
      const right = r.right + 300 < window.innerWidth;
      setTip({ x: right ? r.right + 8 : Math.max(8, r.left - 308), y: Math.min(r.top, window.innerHeight - 220) });
    }, 350);
  };
  const hideTip = () => { clearTimeout(timer.current); setTip(null); };

  return (
    <div
      ref={ref}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onMouseDown={hideTip}
      onClick={onClick}
      style={{
        position: 'relative',
        backgroundColor: selected ? 'rgba(88, 166, 255, 0.16)' : 'var(--surface-1)',
        borderRadius: 'var(--radius-md)',
        padding: '0.6rem 0.7rem',
        cursor: overlay ? 'grabbing' : 'grab',
        borderLeft: `4px solid ${color}`,
        outline: selected ? '2px solid var(--accent-primary)' : '1px solid var(--border-default)',
        outlineOffset: selected ? '-2px' : '-1px',
        opacity: placeholder ? 0.35 : 1,
        boxShadow: overlay ? '0 14px 30px rgba(0,0,0,0.45), 0 4px 10px rgba(0,0,0,0.3)' : 'none',
        transform: overlay ? 'scale(1.03) rotate(1.2deg)' : undefined,
        userSelect: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.25rem', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
        {seq != null && (
          <span style={{ fontWeight: 'bold', color: 'var(--text-primary)', backgroundColor: 'var(--btn-secondary-bg)', borderRadius: '8px', padding: '0 0.35rem' }}>{seq}</span>
        )}
        <span data-card-face="customer" style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '0.78rem', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{face.customer}</span>
        {inProgress && <span style={{ color: 'var(--color-warning)', fontWeight: 'bold' }}>IN PROGRESS</span>}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
          {selected && <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>Selected</span>}
          <span style={{
            padding: '0.05rem 0.4rem', borderRadius: '10px', backgroundColor: color, color: 'var(--text-primary)', maxWidth: '110px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{driverName || 'Unassigned'}</span>
          {rightSlot}
        </span>
      </div>
      <div data-card-face="route" style={{ ...clamp2, fontSize: '0.83rem', color: 'var(--text-primary)', lineHeight: 1.3 }}>{face.route}</div>
      {(routeBadge || draftBadges.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.3rem', fontSize: '0.66rem' }}>
          {routeBadge && (
            <span style={{ padding: '0.05rem 0.35rem', borderRadius: '4px', border: '1px solid var(--border-default)', color: 'var(--text-secondary)' }}>
              On route: {routeBadge}
            </span>
          )}
          {draftBadges.map((b) => (
            <span key={b} style={{ padding: '0.05rem 0.35rem', borderRadius: '4px', border: '1px dashed var(--accent-primary)', color: 'var(--accent-primary)' }}>
              In draft: {b}
            </span>
          ))}
        </div>
      )}
      {groupCount > 1 && (
        <span style={{
          position: 'absolute', top: '-8px', right: '-8px', backgroundColor: 'var(--accent-primary)', color: 'white',
          borderRadius: '12px', padding: '0.1rem 0.45rem', fontSize: '0.72rem', fontWeight: 'bold', boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
        }}>{groupCount} jobs</span>
      )}
      {tip && (
        <div style={{
          position: 'fixed', left: tip.x, top: tip.y, width: '300px', zIndex: 2000, pointerEvents: 'none',
          backgroundColor: 'rgba(13, 17, 23, 0.97)', color: '#e6edf3', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)', padding: '0.6rem 0.7rem', fontSize: '0.75rem', whiteSpace: 'pre-wrap',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)', lineHeight: 1.4,
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.3rem' }}>
            #{load.load_number || String(load.id).slice(0, 8)}{load.customer?.company_name || load.customer_name ? ' - ' + (load.customer?.company_name || load.customer_name) : ''}
          </div>
          {summary.tooltip}
          {load.commodity ? '\nCargo: ' + load.commodity : ''}
        </div>
      )}
    </div>
  );
}

// The same tile, sortable. `id` is the dnd id; `data` rides along to the handlers.
export function SortableJobTile({ id, data, disabled = false, ...tileProps }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, data, disabled });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: transition || 'transform 200ms ease',
        marginBottom: '0.5rem',
        touchAction: 'manipulation',
      }}
      {...attributes}
      {...listeners}
    >
      <JobTile {...tileProps} placeholder={isDragging || tileProps.placeholder} />
    </div>
  );
}

export default JobTile;
